import {
  ExpressionSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import { FILL_PATTERN_LAYER_MIN_ZOOM } from 'features/mapping/mapping.constants';
import {
  FireWeatherPattern,
  FireWeatherPatternSatellite,
  FloodWarningPattern,
  FloodWatchPattern,
  FloodWarningNotAssociatedPattern,
  FloodWatchNotAssociatedPattern,
  FloodWarningPatternSatellite,
  FloodWatchPatternSatellite,
  FloodWarningNotAssociatedPatternSatellite,
  FloodWatchNotAssociatedPatternSatellite,
  RedFlagWarningPattern,
  RedFlagWarningPatternSatellite,
} from 'features/mapping/icons/iconData/warningsAndAlertsIcons';
import {
  MAX_ZOOM_LEVEL,
  NWS_RED_FLAG_WARNING_FILL_COLOR,
  NWS_FIRE_WEATHER_WATCH_FILL_COLOR,
  NWS_FIRE_ALERT_FILL_OPACITY_STREET,
  NWS_FIRE_ALERT_FILL_OPACITY_SATELLITE,
  NWS_RED_FLAG_WARNING_STROKE_COLOR_STREET,
  NWS_FIRE_WEATHER_WATCH_STROKE_COLOR_STREET,
  NWS_RED_FLAG_WARNING_STROKE_COLOR_SATELLITE,
  NWS_FIRE_WEATHER_WATCH_STROKE_COLOR_SATELLITE,
  FLOOD_COLOR,
  FLOOD_WATCH_FILL_COLOR,
  FLOOD_NOT_ASSOCIATED_FILL_COLOR,
  NWS_FLOOD_WARNING_FILL_OPACITY_STREET,
  NWS_FLOOD_WATCH_FILL_OPACITY_STREET,
  NWS_FLOOD_WARNING_FILL_OPACITY_SATELLITE,
  NWS_FLOOD_WATCH_FILL_OPACITY_SATELLITE,
  NWS_FLOOD_WARNING_NOT_ASSOCIATED_FILL_OPACITY_STREET,
  NWS_FLOOD_WATCH_NOT_ASSOCIATED_FILL_OPACITY_STREET,
  NWS_FLOOD_WARNING_NOT_ASSOCIATED_FILL_OPACITY_SATELLITE,
  NWS_FLOOD_WATCH_NOT_ASSOCIATED_FILL_OPACITY_SATELLITE,
  NWS_FLOOD_STROKE_COLOR_STREET,
  NWS_FLOOD_STROKE_COLOR_SATELLITE,
  NWS_FLOOD_NOT_ASSOCIATED_FILL_COLOR_SATELLITE,
  WEA_POLYGON_COLOR,
  NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
  NWS_ALERT_PATTERN_FILL_OPACITY_NOT_ASSOCIATED,
} from './NWSWatchesAndWarningsLayer.constants';

type NwsFireStrokeColors = {
  redFlag: string;
  fireWeatherWatch: string;
};

const getNwsFireStrokeColors = (
  isDarkBaseLayer: boolean,
): NwsFireStrokeColors =>
  isDarkBaseLayer
    ? {
        redFlag: NWS_RED_FLAG_WARNING_STROKE_COLOR_SATELLITE,
        fireWeatherWatch: NWS_FIRE_WEATHER_WATCH_STROKE_COLOR_SATELLITE,
      }
    : {
        redFlag: NWS_RED_FLAG_WARNING_STROKE_COLOR_STREET,
        fireWeatherWatch: NWS_FIRE_WEATHER_WATCH_STROKE_COLOR_STREET,
      };

type NwsFirePatternNames = {
  redFlag: string;
  fireWeatherWatch: string;
};

const getNwsFirePatternNames = (
  isDarkBaseLayer: boolean,
): NwsFirePatternNames =>
  isDarkBaseLayer
    ? {
        redFlag: RedFlagWarningPatternSatellite.name,
        fireWeatherWatch: FireWeatherPatternSatellite.name,
      }
    : {
        redFlag: RedFlagWarningPattern.name,
        fireWeatherWatch: FireWeatherPattern.name,
      };

type NwsFloodPatternNames = {
  warning: string;
  warningNotAssociated: string;
  watch: string;
  watchNotAssociated: string;
};

const getNwsFloodPatternNames = (
  isDarkBaseLayer: boolean,
): NwsFloodPatternNames =>
  isDarkBaseLayer
    ? {
        warning: FloodWarningPatternSatellite.name,
        warningNotAssociated: FloodWarningNotAssociatedPatternSatellite.name,
        watch: FloodWatchPatternSatellite.name,
        watchNotAssociated: FloodWatchNotAssociatedPatternSatellite.name,
      }
    : {
        warning: FloodWarningPattern.name,
        warningNotAssociated: FloodWarningNotAssociatedPattern.name,
        watch: FloodWatchPattern.name,
        watchNotAssociated: FloodWatchNotAssociatedPattern.name,
      };

const getNwsFloodStrokeColor = (isDarkBaseLayer: boolean): string =>
  isDarkBaseLayer
    ? NWS_FLOOD_STROKE_COLOR_SATELLITE
    : NWS_FLOOD_STROKE_COLOR_STREET;

const getNwsFloodNotAssociatedFillColor = (isDarkBaseLayer: boolean): string =>
  isDarkBaseLayer
    ? NWS_FLOOD_NOT_ASSOCIATED_FILL_COLOR_SATELLITE
    : FLOOD_NOT_ASSOCIATED_FILL_COLOR;

export const isNwsFirePolygon: FilterSpecification = [
  '==',
  ['get', 'type'],
  'nws_fire',
];
export const isNwsFloodPolygon: FilterSpecification = [
  '==',
  ['get', 'type'],
  'nws_flood',
];
export const isRedFlagWarning: FilterSpecification = [
  'all',
  ['==', ['get', 'type'], 'nws_fire'],
  ['==', ['get', 'subtype'], 'warning'],
];
export const isFireWeatherWatch: FilterSpecification = [
  'all',
  ['==', ['get', 'type'], 'nws_fire'],
  ['==', ['get', 'subtype'], 'watch'],
];
export const isFloodWarning: FilterSpecification = [
  'all',
  ['==', ['get', 'type'], 'nws_flood'],
  ['==', ['get', 'subtype'], 'warning'],
];
export const isFloodWatch: FilterSpecification = [
  'all',
  ['==', ['get', 'type'], 'nws_flood'],
  ['==', ['get', 'subtype'], 'watch'],
];
export const isExteriorShell: FilterSpecification = [
  'get',
  'is_exterior_shell',
];
export const isFill: FilterSpecification = ['get', 'is_fill'];
export const isWeaPolygon: FilterSpecification = ['==', ['get', 'type'], 'wea'];
export const isHighlightedPolygon: FilterSpecification = [
  'get',
  'highlight_on_map',
];

// Style functions accept these options so the same builders work for both the
// tile-source NWSWarningsAndAlertsLayer (which sets sourceLayer + maxZoom) and
// the GeoJSON-source SelectedAlertPolygonLayer (which sets neither).
type StyleOptions = {
  sourceLayer?: string;
  maxZoom?: number;
  // Fill the exterior shell feature instead of the fill feature (ignore evac zone cutouts)
  fillShell?: boolean;
};

const withSource = (
  style: LayerProps,
  { sourceLayer, maxZoom }: StyleOptions,
): LayerProps =>
  ({
    ...style,
    ...(sourceLayer ? { 'source-layer': sourceLayer } : {}),
    ...(maxZoom !== undefined ? { maxzoom: maxZoom } : {}),
  }) as LayerProps;

export const getAssociatedFilter = (
  selectedGeoEventId?: number,
): FilterSpecification | null =>
  selectedGeoEventId
    ? [
        'in',
        `,${selectedGeoEventId},`,
        ['concat', ',', ['coalesce', ['get', 'geo_event_ids'], ''], ','],
      ]
    : null;

const getNwsFireAlertFillOpacity = (isDarkBaseLayer: boolean): number =>
  isDarkBaseLayer
    ? NWS_FIRE_ALERT_FILL_OPACITY_SATELLITE
    : NWS_FIRE_ALERT_FILL_OPACITY_STREET;

const getNwsFloodWarningFillOpacity = (isDarkBaseLayer: boolean): number =>
  isDarkBaseLayer
    ? NWS_FLOOD_WARNING_FILL_OPACITY_SATELLITE
    : NWS_FLOOD_WARNING_FILL_OPACITY_STREET;

const getNwsFloodWatchFillOpacity = (isDarkBaseLayer: boolean): number =>
  isDarkBaseLayer
    ? NWS_FLOOD_WATCH_FILL_OPACITY_SATELLITE
    : NWS_FLOOD_WATCH_FILL_OPACITY_STREET;

const getNwsFloodWarningNotAssociatedFillOpacity = (
  isDarkBaseLayer: boolean,
): number =>
  isDarkBaseLayer
    ? NWS_FLOOD_WARNING_NOT_ASSOCIATED_FILL_OPACITY_SATELLITE
    : NWS_FLOOD_WARNING_NOT_ASSOCIATED_FILL_OPACITY_STREET;

const getNwsFloodWatchNotAssociatedFillOpacity = (
  isDarkBaseLayer: boolean,
): number =>
  isDarkBaseLayer
    ? NWS_FLOOD_WATCH_NOT_ASSOCIATED_FILL_OPACITY_SATELLITE
    : NWS_FLOOD_WATCH_NOT_ASSOCIATED_FILL_OPACITY_STREET;

const getDefaultFillOpacity = (
  isDarkBaseLayer: boolean,
): ExpressionSpecification => {
  const nwsFireFillOpacity = getNwsFireAlertFillOpacity(isDarkBaseLayer);
  const nwsFloodWarningFillOpacity =
    getNwsFloodWarningFillOpacity(isDarkBaseLayer);
  const nwsFloodWatchFillOpacity = getNwsFloodWatchFillOpacity(isDarkBaseLayer);
  return [
    'case',
    isRedFlagWarning,
    nwsFireFillOpacity,
    isFireWeatherWatch,
    nwsFireFillOpacity,
    isFloodWarning,
    nwsFloodWarningFillOpacity,
    isFloodWatch,
    nwsFloodWatchFillOpacity,
    0.2,
  ];
};

const getNotAssociatedFillOpacity = (
  isDarkBaseLayer: boolean,
): ExpressionSpecification => {
  const nwsFireFillOpacity = getNwsFireAlertFillOpacity(isDarkBaseLayer);
  const nwsFloodWarningNotAssociatedFillOpacity =
    getNwsFloodWarningNotAssociatedFillOpacity(isDarkBaseLayer);
  const nwsFloodWatchNotAssociatedFillOpacity =
    getNwsFloodWatchNotAssociatedFillOpacity(isDarkBaseLayer);
  return [
    'case',
    isRedFlagWarning,
    nwsFireFillOpacity,
    isFireWeatherWatch,
    nwsFireFillOpacity,
    isFloodWarning,
    nwsFloodWarningNotAssociatedFillOpacity,
    isFloodWatch,
    nwsFloodWatchNotAssociatedFillOpacity,
    0.1,
  ];
};

export const getFillStyle = (
  filter: FilterSpecification,
  isDarkBaseLayer: boolean,
  selectedGeoEventId?: number,
  options: StyleOptions = {},
): LayerProps => {
  const associatedFilter = getAssociatedFilter(selectedGeoEventId);
  const defaultFillOpacity = getDefaultFillOpacity(isDarkBaseLayer);
  const notAssociatedFillOpacity = getNotAssociatedFillOpacity(isDarkBaseLayer);
  const nwsFloodNotAssociatedFillColor =
    getNwsFloodNotAssociatedFillColor(isDarkBaseLayer);

  const fillOpacity: ExpressionSpecification = associatedFilter
    ? ['case', associatedFilter, defaultFillOpacity, notAssociatedFillOpacity]
    : defaultFillOpacity;

  return withSource(
    {
      type: 'fill',
      paint: {
        'fill-color': [
          'case',
          isRedFlagWarning,
          NWS_RED_FLAG_WARNING_FILL_COLOR,
          isFireWeatherWatch,
          NWS_FIRE_WEATHER_WATCH_FILL_COLOR,
          isFloodWarning,
          associatedFilter
            ? [
                'case',
                associatedFilter,
                FLOOD_COLOR,
                nwsFloodNotAssociatedFillColor,
              ]
            : FLOOD_COLOR,
          isFloodWatch,
          associatedFilter
            ? [
                'case',
                associatedFilter,
                FLOOD_WATCH_FILL_COLOR,
                nwsFloodNotAssociatedFillColor,
              ]
            : FLOOD_WATCH_FILL_COLOR,
          FLOOD_COLOR,
        ],
        'fill-opacity': fillOpacity,
      },
      layout: {
        // Negate rank so lower rank = higher sort key = rendered on top.
        'fill-sort-key': ['*', ['coalesce', ['get', 'rank'], 0], -1],
      },
      filter: ['all', filter, options.fillShell ? isExteriorShell : isFill],
    },
    options,
  );
};

export const getPatternFillStyle = (
  filter: FilterSpecification,
  isDarkBaseLayer: boolean,
  selectedGeoEventId?: number,
  options: StyleOptions = {},
): LayerProps => {
  const associatedFilter = getAssociatedFilter(selectedGeoEventId);
  const nwsFirePatternNames = getNwsFirePatternNames(isDarkBaseLayer);
  const nwsFloodPatternNames = getNwsFloodPatternNames(isDarkBaseLayer);

  const floodWarningPattern = associatedFilter
    ? [
        'case',
        associatedFilter,
        nwsFloodPatternNames.warning,
        nwsFloodPatternNames.warningNotAssociated,
      ]
    : nwsFloodPatternNames.warning;

  const floodWatchPattern = associatedFilter
    ? [
        'case',
        associatedFilter,
        nwsFloodPatternNames.watch,
        nwsFloodPatternNames.watchNotAssociated,
      ]
    : nwsFloodPatternNames.watch;

  const patternFillOpacity: ExpressionSpecification | number = associatedFilter
    ? [
        'case',
        associatedFilter,
        NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
        NWS_ALERT_PATTERN_FILL_OPACITY_NOT_ASSOCIATED,
      ]
    : NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED;

  return withSource(
    {
      type: 'fill',
      minzoom: FILL_PATTERN_LAYER_MIN_ZOOM,
      paint: {
        'fill-pattern': [
          'case',
          isRedFlagWarning,
          nwsFirePatternNames.redFlag,
          isFireWeatherWatch,
          nwsFirePatternNames.fireWeatherWatch,
          isFloodWarning,
          floodWarningPattern,
          floodWatchPattern,
        ],
        'fill-opacity': patternFillOpacity,
      },
      filter: ['all', filter, options.fillShell ? isExteriorShell : isFill],
    },
    options,
  );
};

const defaultStrokeOpacity: ExpressionSpecification = [
  'case',
  isRedFlagWarning,
  1,
  isFireWeatherWatch,
  1,
  isFloodWarning,
  1,
  isFloodWatch,
  1,
  0.6,
];

const notAssociatedStrokeOpacity: ExpressionSpecification = [
  'case',
  isRedFlagWarning,
  1,
  isFireWeatherWatch,
  1,
  isFloodWarning,
  0.4,
  0.2,
];

export const getStrokeStyle = (
  filter: FilterSpecification,
  isDarkBaseLayer: boolean,
  selectedGeoEventId?: number,
  options: StyleOptions = {},
): LayerProps => {
  const associatedFilter = getAssociatedFilter(selectedGeoEventId);
  const nwsFireStrokeColors = getNwsFireStrokeColors(isDarkBaseLayer);
  const nwsFloodStrokeColor = getNwsFloodStrokeColor(isDarkBaseLayer);

  const lineOpacity: ExpressionSpecification = associatedFilter
    ? [
        'case',
        associatedFilter,
        defaultStrokeOpacity,
        notAssociatedStrokeOpacity,
      ]
    : defaultStrokeOpacity;

  return withSource(
    {
      type: 'line',
      paint: {
        'line-color': [
          'case',
          isRedFlagWarning,
          nwsFireStrokeColors.redFlag,
          isFireWeatherWatch,
          nwsFireStrokeColors.fireWeatherWatch,
          nwsFloodStrokeColor,
        ],
        'line-opacity': lineOpacity,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.25, 18, 3.25],
        'line-dasharray': [2, 1],
      },
      filter: ['all', filter, isExteriorShell],
    },
    options,
  );
};

export const getClickableFillStyle = (
  filterExpression: FilterSpecification,
  showWeaPolygons: boolean,
  options: StyleOptions = {},
): LayerProps =>
  withSource(
    {
      type: 'fill',
      paint: {
        'fill-opacity': 0,
      },
      filter: [
        'all',
        isExteriorShell,
        // Only clickable while visible.
        ['<', ['zoom'], MAX_ZOOM_LEVEL],
        ['any', ['all', showWeaPolygons, isWeaPolygon], filterExpression],
      ],
    },
    options,
  );

export const getWeaFillStyle = (
  visible: boolean,
  selectedGeoEventId?: number,
  options: StyleOptions = {},
): LayerProps => {
  const associatedFilter = getAssociatedFilter(selectedGeoEventId);

  const fillOpacity: ExpressionSpecification | number = associatedFilter
    ? ['case', associatedFilter, 0.4, 0.3]
    : 0.4;

  return withSource(
    {
      type: 'fill',
      paint: {
        'fill-color': WEA_POLYGON_COLOR,
        'fill-opacity': fillOpacity,
      },
      filter: ['all', visible, isWeaPolygon, isFill],
    },
    options,
  );
};

export const getWeaPatternFillStyle = (
  visible: boolean,
  options: StyleOptions = {},
): LayerProps =>
  withSource(
    {
      type: 'fill',
      minzoom: FILL_PATTERN_LAYER_MIN_ZOOM,
      paint: {
        'fill-pattern': 'WeaPolygonPattern',
      },
      filter: ['all', visible, isWeaPolygon, isFill],
    },
    options,
  );

export const getWeaStrokeStyle = (
  visible: boolean,
  options: StyleOptions = {},
): LayerProps =>
  withSource(
    {
      type: 'line',
      paint: {
        'line-color': WEA_POLYGON_COLOR,
        'line-opacity': 1,
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.25, 18, 3.25],
      },
      filter: ['all', visible, isWeaPolygon, isExteriorShell],
    },
    options,
  );

export const getPolygonHighlightStrokeStyle = (
  isNwsFireLayerVisible: boolean,
  isNwsFloodLayerVisible: boolean,
  selectedAlertId?: number | string,
  options: StyleOptions = {},
): LayerProps => {
  const { maxZoom, ...optionsWithoutMaxZoom } = options;
  const baseStyle = getWeaStrokeStyle(true, optionsWithoutMaxZoom);
  return {
    ...baseStyle,
    type: 'line',
    paint: {
      ...baseStyle.paint,
      'line-offset': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 8],
    },
    filter: [
      'all',
      isHighlightedPolygon,
      isExteriorShell,
      [
        'any',
        isWeaPolygon,
        ['all', isNwsFireLayerVisible, isNwsFirePolygon],
        ['all', isNwsFloodLayerVisible, isNwsFloodPolygon],
      ],
      ['any', ['==', ['id'], selectedAlertId], ['<', ['zoom'], MAX_ZOOM_LEVEL]],
    ],
  };
};
